import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface UpdateStatus {
  updateAvailable: boolean;
  updateDownloaded: boolean;
  isInitialized: boolean;
}

interface UpdateInfo {
  version: string;
  releaseNotes?: string;
}

interface UpdateError {
  message: string;
  isUserInitiated: boolean;
}

const UpdateChecker: React.FC = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [error, setError] = useState<UpdateError | null>(null);

  useEffect(() => {
    // Listen for auto-updater events
    const handleUpdateChecking = () => {
      setIsChecking(true);
      setError(null);
    };

    const handleUpdateAvailable = (info: UpdateInfo) => {
      setIsChecking(false);
      setUpdateInfo(info);
      setUpdateStatus(prev => prev ? { ...prev, updateAvailable: true } : null);
      
      // Show toast notification for new update
      toast({
        title: "Update Available",
        description: `Version ${info.version} is available for download.`,
        variant: "default"
      });
    };

    const handleUpdateNotAvailable = () => {
      setIsChecking(false);
      setError(null);
      setUpdateStatus(prev => prev ? { ...prev, updateAvailable: false } : null);
    };

    const handleUpdateError = (error: UpdateError) => {
      setIsChecking(false);
      setError(error);
      
      // Only show toast for user-initiated checks or critical errors
      if (error.isUserInitiated) {
        toast({
          title: "Update Check Failed",
          description: error.message,
          variant: "destructive"
        });
      }
    };

    const handleUpdateDownloadProgress = () => {
      setIsDownloading(true);
    };

    const handleUpdateDownloaded = (info: UpdateInfo) => {
      setIsDownloading(false);
      setUpdateInfo(info);
      setUpdateStatus(prev => prev ? { ...prev, updateDownloaded: true } : null);
      
      toast({
        title: "Update Ready",
        description: "Update has been downloaded and is ready to install.",
        variant: "default"
      });
    };

    // Add event listeners
    if (window.electronAPI) {
      window.electronAPI.on('auto-updater:update-checking', handleUpdateChecking);
      window.electronAPI.on('auto-updater:update-available', handleUpdateAvailable);
      window.electronAPI.on('auto-updater:update-not-available', handleUpdateNotAvailable);
      window.electronAPI.on('auto-updater:update-error', handleUpdateError);
      window.electronAPI.on('auto-updater:update-download-progress', handleUpdateDownloadProgress);
      window.electronAPI.on('auto-updater:update-downloaded', handleUpdateDownloaded);
    }

    // Get initial status
    getUpdateStatus();

    return () => {
      // Cleanup event listeners
      if (window.electronAPI) {
        window.electronAPI.removeAllListeners('auto-updater:update-checking');
        window.electronAPI.removeAllListeners('auto-updater:update-available');
        window.electronAPI.removeAllListeners('auto-updater:update-not-available');
        window.electronAPI.removeAllListeners('auto-updater:update-error');
        window.electronAPI.removeAllListeners('auto-updater:update-download-progress');
        window.electronAPI.removeAllListeners('auto-updater:update-downloaded');
      }
    };
  }, []);

  const getUpdateStatus = async () => {
    try {
      if (window.electronAPI) {
        const status = await window.electronAPI.invoke('get-update-status');
        if (status.success) {
          setUpdateStatus(status);
        }
      }
    } catch (error) {
      console.error('Failed to get update status:', error);
      // Don't show error UI for background status checks
    }
  };

  const checkForUpdates = async () => {
    try {
      setIsChecking(true);
      setError(null);
      
      if (window.electronAPI) {
        const result = await window.electronAPI.invoke('check-for-updates');
        if (!result.success) {
          setError({ 
            message: result.error || 'Failed to check for updates',
            isUserInitiated: true
          });
        }
      }
    } catch (error) {
      setError({ 
        message: error instanceof Error ? error.message : 'Failed to check for updates',
        isUserInitiated: true
      });
      console.error('Check for updates error:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const downloadUpdate = async () => {
    try {
      setIsDownloading(true);
      setError(null);
      
      if (window.electronAPI) {
        const result = await window.electronAPI.invoke('download-update');
        if (!result.success) {
          setError({ 
            message: result.error || 'Failed to download update',
            isUserInitiated: true
          });
          toast({
            title: "Download Failed",
            description: result.error || 'Failed to download update',
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to download update';
      setError({ message: errorMessage, isUserInitiated: true });
      toast({
        title: "Download Failed",
        description: errorMessage,
        variant: "destructive"
      });
      console.error('Download update error:', error);
    }
  };

  const installUpdate = async () => {
    try {
      if (window.electronAPI) {
        await window.electronAPI.invoke('install-update');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to install update';
      setError({ message: errorMessage, isUserInitiated: true });
      toast({
        title: "Installation Failed",
        description: errorMessage,
        variant: "destructive"
      });
      console.error('Install update error:', error);
    }
  };

  const getStatusBadge = () => {
    if (!updateStatus) return null;
    
    if (updateStatus.updateDownloaded) {
      return <Badge className="bg-green-100 text-green-800">Update Ready</Badge>;
    } else if (updateStatus.updateAvailable) {
      return <Badge className="bg-blue-100 text-blue-800">Update Available</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-800">Up to Date</Badge>;
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Software Updates
        </CardTitle>
        <CardDescription>
          Check for the latest version of Hospital Management System
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Display */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Status:</span>
          {getStatusBadge()}
        </div>

        {/* Update Info */}
        {updateInfo && updateStatus?.updateAvailable && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Version {updateInfo.version} available</span>
            </div>
            {updateInfo.releaseNotes && (
              <p className="text-xs text-blue-700">{updateInfo.releaseNotes}</p>
            )}
          </div>
        )}

        {/* Error Display - Only show for user-initiated errors */}
        {error?.isUserInitiated && (
          <div className="p-3 bg-red-50 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-700">{error.message}</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          <Button
            onClick={checkForUpdates}
            disabled={isChecking || isDownloading}
            className="w-full"
          >
            {isChecking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Check for Updates
              </>
            )}
          </Button>

          {updateStatus?.updateAvailable && !updateStatus?.updateDownloaded && (
            <Button
              onClick={downloadUpdate}
              disabled={isDownloading}
              variant="outline"
              className="w-full"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Download Update
                </>
              )}
            </Button>
          )}

          {updateStatus?.updateDownloaded && (
            <Button
              onClick={installUpdate}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Install Update
            </Button>
          )}
        </div>

        {/* Current Version */}
        <div className="text-xs text-gray-500 text-center">
          Current Version: 1.0.0
        </div>
      </CardContent>
    </Card>
  );
};

export default UpdateChecker; 